import ChevronDownIcon from '@src/svgs/ChevronDownIcon';
import EditVideoIcon from '@src/svgs/EditVideoIcon';
import styled, { keyframes } from 'styled-components';

import { useEditVideoMenu } from './hooks/useEditVideoMenu';

import type { FC } from 'react';

/**
 * "Edit Video" split button overlaid on the Content Studio player. The main
 * segment reopens the last-edited clip; the caret opens the "open full video
 * in …" dropdown. All behaviour lives in {@link useEditVideoMenu}.
 */
const EditVideoButton: FC = () => {
  const {
    menuRef,
    menuOpen,
    toggleMenu,
    closeMenu,
    isCreatingContentCopy,
    isDownloadingOriginal,
    onEditVideoClick,
    onOpenCreatorStudio,
    onOpenInContentStudio,
    onDownloadOriginal,
  } = useEditVideoMenu();

  return (
    <PlayerOverlay>
      <EditVideoDropdown ref={menuRef}>
        <EditVideoSplitButton>
          <EditVideoMainSegment
            onClick={onEditVideoClick}
            aria-label="Edit Video"
            disabled={isCreatingContentCopy}
          >
            {isCreatingContentCopy ? <ButtonSpinner /> : <EditVideoIcon />}
            {isCreatingContentCopy ? 'Opening…' : 'Edit Video'}
          </EditVideoMainSegment>
          <EditVideoCaretSegment
            onClick={toggleMenu}
            aria-label="Open full video options"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            disabled={isCreatingContentCopy}
          >
            <ChevronDownIcon strokeColor="white" />
          </EditVideoCaretSegment>
        </EditVideoSplitButton>
        {menuOpen && (
          <EditVideoMenu role="menu">
            <EditVideoMenuHeader>Open full video in</EditVideoMenuHeader>
            <EditVideoMenuItem
              role="menuitem"
              onClick={() => {
                closeMenu();
                onOpenCreatorStudio();
              }}
            >
              Creator Studio
            </EditVideoMenuItem>
            <EditVideoMenuItem role="menuitem" onClick={onOpenInContentStudio}>
              Content Studio
            </EditVideoMenuItem>
            <EditVideoMenuDivider role="separator" />
            <EditVideoMenuItem
              role="menuitem"
              onClick={onDownloadOriginal}
              disabled={isDownloadingOriginal}
            >
              {isDownloadingOriginal ? <ButtonSpinner /> : null}
              {isDownloadingOriginal ? 'Downloading…' : 'Download Original'}
            </EditVideoMenuItem>
          </EditVideoMenu>
        )}
      </EditVideoDropdown>
    </PlayerOverlay>
  );
};

export default EditVideoButton;

const PlayerOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 10;
`;

const EditVideoDropdown = styled.div`
  position: absolute;
  top: 6px;
  right: 6px;
  pointer-events: auto;
`;

const EditVideoSplitButton = styled.div`
  display: flex;
  align-items: stretch;
  background-color: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 5px;
  overflow: hidden;
  transition: border-color var(--transition-normal) ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.5);
  }
`;

const EditVideoSegment = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  background: none;
  border: none;
  color: white;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: background-color var(--transition-normal) ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }

  &:active {
    opacity: 0.85;
  }

  &:disabled {
    cursor: default;
    opacity: 0.8;
  }
`;

const EditVideoMainSegment = styled(EditVideoSegment)``;

const EditVideoCaretSegment = styled(EditVideoSegment)`
  padding: 5px 6px;
  border-left: 1px solid rgba(255, 255, 255, 0.3);
`;

const EditVideoMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  display: flex;
  flex-direction: column;
  min-width: 140px;
  padding: 4px;
  background-color: rgba(30, 30, 30, 0.85);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 5px;
  z-index: 20;
`;

const EditVideoMenuHeader = styled.div`
  padding: 4px 10px 6px;
  color: rgba(255, 255, 255, 0.55);
  font-size: 10px;
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
`;

const EditVideoMenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 6px 10px;
  background: none;
  border: none;
  border-radius: 4px;
  color: white;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  text-align: left;
  cursor: pointer;
  transition: background-color var(--transition-normal) ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.18);
  }

  &:disabled {
    cursor: default;
    opacity: 0.7;

    &:hover {
      background-color: transparent;
    }
  }
`;

const EditVideoMenuDivider = styled.div`
  height: 1px;
  margin: 4px 6px;
  background-color: rgba(255, 255, 255, 0.15);
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const ButtonSpinner = styled.span`
  width: 12px;
  height: 12px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: white;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;
